import { PrimaryButton, SecondaryButton } from 'components/v2/buttons';
import PrimeCheckbox from 'components/v2/controls/PrimeCheckbox';
import { HelpBox, HelpBoxBoldText } from 'components/v2/help-boxes';
import { Dialog } from 'primereact/dialog';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { recordCompilerSurveyResponse } from 'services/api/compilerSurveyApi';
import styled from 'styled-components';
import { CheckBoxPanel } from 'styles/v2/Styles.styled';
import { CompilerSurveyTrigger } from 'types/types';

type Props = {
  visible: boolean;
  trigger: CompilerSurveyTrigger;
  surveyUrl: string;
  onClose: () => void;
};

const thankYouKeyByTrigger: Record<CompilerSurveyTrigger, string> = {
  StartVerification: 'compilerSurvey.thankYouVerification',
  SendToPublish: 'compilerSurvey.thankYouPublication',
};

/**
 * Keyed by invitation, so every invitation starts with fresh state. Closing hides it through `visible` instead of
 * unmounting it: PrimeReact 9 releases the `blockScroll` lock on the body only in the hide transition.
 */
const CompilerSurveyDialog: React.FC<Props> = ({ visible, trigger, surveyUrl, onClose }) => {
  const { t } = useTranslation();
  const [dontShowAgain, setDontShowAgain] = useState<boolean>(false);
  const actedRef = useRef(false);

  const handleStartSurvey = () => {
    if (actedRef.current) {
      return;
    }
    actedRef.current = true;
    window.open(surveyUrl, '_blank', 'noopener,noreferrer');
    onClose();
    recordCompilerSurveyResponse({ action: 'StartSurvey', dontShowAgain }).catch(() => {});
  };

  const handleNoThanks = () => {
    if (actedRef.current) {
      return;
    }
    actedRef.current = true;
    onClose();
    if (dontShowAgain) {
      recordCompilerSurveyResponse({ action: 'Decline', dontShowAgain: true }).catch(() => {});
    }
  };

  const footer = (
    <ButtonsRow>
      <SecondaryButton label={t('compilerSurvey.noThanks')} onClick={handleNoThanks} />
      <PrimaryButton label={t('compilerSurvey.startSurvey')} onClick={handleStartSurvey} />
    </ButtonsRow>
  );

  return (
    <StyledDialog
      header={t('compilerSurvey.title')}
      visible={visible}
      modal
      blockScroll
      closable={false}
      closeOnEscape={false}
      dismissableMask={false}
      draggable={false}
      resizable={false}
      style={{ width: '560px', maxWidth: '95vw' }}
      footer={footer}
      onHide={() => {}}
    >
      <SurveyHelpBox>
        <HelpBoxBoldText>{t(thankYouKeyByTrigger[trigger])}</HelpBoxBoldText>
        <p>{t('compilerSurvey.body')}</p>
      </SurveyHelpBox>
      <SurveyCheckBoxPanel>
        <PrimeCheckbox
          id="compilerSurveyDontShowAgain"
          name="compilerSurveyDontShowAgain"
          value={dontShowAgain}
          label={t('compilerSurvey.dontShowAgain')}
          labelStyle={{ fontSize: '14px' }}
          boxStyle={{ margin: 0 }}
          onChange={(event: any) => setDontShowAgain(!!event.checked)}
        />
      </SurveyCheckBoxPanel>
    </StyledDialog>
  );
};

export default CompilerSurveyDialog;

const StyledDialog = styled(Dialog)`
  border-radius: 4px;
  overflow: hidden;

  && .p-dialog-header {
    justify-content: center;
    padding: 30px 30px 30px 30px;
    background-color: ${(props) => props.theme.colors.primaryWhite};
  }
  && .p-dialog-title {
    margin: 0;
    text-align: center;
    ${(props) => props.theme.typography.headerH4};
    line-height: 18px;
    letter-spacing: -0.02em;
  }
  && .p-dialog-content {
    padding: 0 30px;
  }
  && .p-dialog-footer {
    padding: 30px;
  }
`;

const SurveyHelpBox = styled(HelpBox)`
  padding: 16px;
  gap: 8px;

  p {
    margin: 0;
  }
`;

const SurveyCheckBoxPanel = styled(CheckBoxPanel)`
  margin-top: 16px;
  padding: 0;
  height: auto;
  gap: 8px;
  background-color: transparent;

  label {
    color: ${(props) => props.theme.colors.primaryBlack70};
  }
  .p-checkbox {
    margin-right: 0;
  }
  .p-checkbox,
  .p-checkbox .p-checkbox-box {
    width: 14px;
    height: 14px;
  }
  .p-checkbox .p-checkbox-box .p-checkbox-icon {
    font-size: 10px;
    font-weight: 700;
  }
`;

const ButtonsRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;

  && .p-button {
    margin: 0;
    width: 150px;
    min-width: 150px;
    height: 48px;
    padding: 16px;
  }
`;
